import { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { MessageCircle, X, Send, Bot, User, Loader2 } from 'lucide-react';
import api from '../../lib/api';

export const PartnerChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [config, setConfig] = useState(null);
  const [contactData, setContactData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Load chatbot config
    const loadConfig = async () => {
      try {
        const res = await api.get('/chatbot/config/public');
        setConfig(res.data);
        if (res.data.enabled && res.data.welcome_message) {
          setMessages([{
            role: 'assistant',
            content: res.data.welcome_message
          }]);
        }
      } catch (error) {
        console.log('Chatbot config not available');
      }
    };
    loadConfig();
  }, []);

  useEffect(() => {
    // Scroll to bottom on new messages
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    // Add empty assistant message that we'll stream into
    setMessages(prev => [...prev, { role: 'assistant', content: '', isStreaming: true }]);

    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${baseUrl}/api/chatbot/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
          conversation_history: messages
        })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'content') {
                fullContent += data.content;
                // Update the last message with streamed content
                setMessages(prev => {
                  const newMessages = [...prev];
                  const lastIdx = newMessages.length - 1;
                  if (newMessages[lastIdx]?.isStreaming) {
                    newMessages[lastIdx] = { 
                      role: 'assistant', 
                      content: fullContent, 
                      isStreaming: true 
                    };
                  }
                  return newMessages;
                });
              } else if (data.type === 'done') {
                // Finalize the message
                setMessages(prev => {
                  const newMessages = [...prev];
                  const lastIdx = newMessages.length - 1;
                  if (newMessages[lastIdx]?.isStreaming) {
                    newMessages[lastIdx] = { 
                      role: 'assistant', 
                      content: fullContent,
                      isStreaming: false 
                    };
                  }
                  return newMessages;
                });
                
                // Check if chatbot is requesting contact info
                if (data.requires_contact) {
                  setShowContactForm(true);
                }
              } else if (data.type === 'error') {
                setMessages(prev => {
                  const newMessages = [...prev];
                  const lastIdx = newMessages.length - 1;
                  newMessages[lastIdx] = { 
                    role: 'assistant', 
                    content: data.content || 'Si è verificato un errore.'
                  };
                  return newMessages;
                });
              }
            } catch (e) {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }
    } catch (error) {
      console.error('Stream error:', error);
      setMessages(prev => {
        const newMessages = [...prev];
        const lastIdx = newMessages.length - 1;
        if (newMessages[lastIdx]?.isStreaming) {
          newMessages[lastIdx] = { 
            role: 'assistant', 
            content: 'Mi scuso, si è verificato un errore. Riprova più tardi o contattaci direttamente.'
          };
        }
        return newMessages;
      });
    } finally {
      setIsLoading(false);
    }
  };

  const submitContactForm = async () => {
    if (!contactData.name || !contactData.email || !contactData.company) {
      return;
    }

    setIsSubmittingContact(true);
    try {
      const res = await api.post('/chatbot/lead', {
        session_id: sessionId,
        ...contactData
      });

      if (res.data.success) {
        setShowContactForm(false);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: res.data.message
        }]);
        setContactData({ name: '', email: '', company: '', phone: '', message: '' });
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: res.data.message || 'Si è verificato un errore. Riprova.'
        }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Errore nell\'invio dei dati. Riprova o contattaci direttamente.'
      }]);
    } finally {
      setIsSubmittingContact(false);
    }
  };

  if (!config?.enabled) {
    return null;
  }

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-24 w-16 h-16 bg-primary rounded-full shadow-lg hover:bg-primary/90 transition-all hover:scale-105 flex items-center justify-center z-50"
          data-testid="chatbot-toggle-btn"
        >
          <MessageCircle size={28} className="text-primary-foreground" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-6 right-6 w-[380px] max-w-[calc(100vw-48px)] h-[600px] max-h-[calc(100vh-100px)] bg-gray-900 rounded-2xl shadow-2xl border border-white/10 flex flex-col z-50 overflow-hidden"
          data-testid="chatbot-window"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10 bg-primary/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <Bot size={20} className="text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Assistente SIOS</h3>
                <p className="text-xs text-muted-foreground">Partner Support</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, index) => (
              <div
                key={msg.id || `msg-${index}-${msg.role}`}
                className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${
                  msg.role === 'user' ? 'bg-primary/20' : 'bg-white/10'
                }`}>
                  {msg.role === 'user' ? (
                    <User size={16} className="text-primary" />
                  ) : (
                    <Bot size={16} className="text-white/70" />
                  )}
                </div>
                <div className={`max-w-[80%] p-3 rounded-2xl ${
                  msg.role === 'user' 
                    ? 'bg-primary text-primary-foreground rounded-tr-sm' 
                    : 'bg-white/10 rounded-tl-sm'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">
                    {msg.content}
                    {msg.isStreaming && (
                      <span className="inline-block w-2 h-4 bg-primary/70 ml-1 animate-pulse" />
                    )}
                  </p>
                </div>
              </div>
            ))}

            {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                  <Bot size={16} className="text-white/70" />
                </div>
                <div className="bg-white/10 p-3 rounded-2xl rounded-tl-sm">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            {/* Contact Form */}
            {showContactForm && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
                <p className="text-sm font-medium text-primary">Lascia i tuoi contatti</p>
                <Input
                  placeholder="Nome *"
                  value={contactData.name}
                  onChange={(e) => setContactData(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-white/5 border-white/10 text-sm"
                />
                <Input
                  type="email"
                  placeholder="Email *"
                  value={contactData.email}
                  onChange={(e) => setContactData(prev => ({ ...prev, email: e.target.value }))}
                  className="bg-white/5 border-white/10 text-sm"
                />
                <Input
                  placeholder="Azienda *"
                  value={contactData.company}
                  onChange={(e) => setContactData(prev => ({ ...prev, company: e.target.value }))}
                  className="bg-white/5 border-white/10 text-sm"
                />
                <Input
                  placeholder="Telefono (opzionale)"
                  value={contactData.phone}
                  onChange={(e) => setContactData(prev => ({ ...prev, phone: e.target.value }))}
                  className="bg-white/5 border-white/10 text-sm"
                />
                <textarea
                  placeholder="La tua richiesta (opzionale)"
                  value={contactData.message}
                  onChange={(e) => setContactData(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full p-2 rounded-md bg-white/5 border border-white/10 text-sm resize-none h-16"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={submitContactForm}
                    disabled={isSubmittingContact || !contactData.name || !contactData.email || !contactData.company}
                    className="flex-1"
                    size="sm"
                  >
                    {isSubmittingContact ? (
                      <Loader2 size={16} className="animate-spin mr-2" />
                    ) : null}
                    Invia
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowContactForm(false)}
                  >
                    Dopo
                  </Button>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-white/10">
            <div className="flex gap-2">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Scrivi un messaggio..."
                className="bg-white/5 border-white/10"
                disabled={isLoading}
                data-testid="chatbot-input"
              />
              <Button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isLoading}
                size="icon"
                data-testid="chatbot-send-btn"
              >
                <Send size={18} />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PartnerChatbot;
